"use client";

import { Header, TitleH2 } from "@/components/shared/public";
import ScrollReveal from "@/components/shared/public/ScrollReveal";
import { Link } from "@/i18n/navigation";
import { ArrowUpRight, Globe2, PlayCircle, Youtube } from "lucide-react";
import { useTranslations } from "next-intl";
import Image, { type StaticImageData } from "next/image";
import { JSX } from "react";
import header from "../../../../../public/images/blogs/spacelutions/jan-and-lucas.png";
import section37 from "../../../../../public/images/blogs/spacelutions/video-37.jpg";
import section38 from "../../../../../public/images/blogs/spacelutions/video-38.jpg";
import section39 from "../../../../../public/images/blogs/spacelutions/video-39.jpg";
import section40 from "../../../../../public/images/blogs/spacelutions/video-40.jpg";

const SPACELUTIONS_YOUTUBE_URL =
  "https://www.youtube.com/@Spacelutions-Deutschland";
const SPACELUTIONS_SITE_URL = "https://spacelutions.de/";

const ListenButton = ({
  href,
  icon,
  label,
  description,
}: {
  href: string;
  icon: JSX.Element;
  label: string;
  description: string;
}) => (
  <Link
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="border-input hover:border-primary hover:text-primary flex items-center gap-4 rounded-lg border-2 px-5 py-4 transition-all duration-300"
  >
    <span className="text-primary flex h-12 w-12 shrink-0 items-center justify-center">
      {icon}
    </span>
    <span className="flex flex-col">
      <span className="text-foreground text-base font-semibold">{label}</span>
      <span className="text-ring text-sm">{description}</span>
    </span>
  </Link>
);

const TopicCard = ({ title, text }: { title: string; text: string }) => (
  <div className="border-input bg-background flex flex-col gap-2 rounded-lg border p-5">
    <h3 className="text-primary text-base font-semibold tracking-wide">
      {title}
    </h3>
    <p className="text-ring text-sm leading-relaxed">{text}</p>
  </div>
);

const VideoCard = ({
  src,
  alt,
  title,
  text,
  href,
}: {
  src: StaticImageData;
  alt: string;
  title: string;
  text: string;
  href: string;
}) => (
  <Link
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="group border-input bg-background hover:border-primary flex h-full flex-col overflow-hidden rounded-lg border transition-all duration-300 hover:-translate-y-1"
  >
    <span className="relative block aspect-video overflow-hidden">
      <Image
        src={src}
        alt={alt}
        fill
        sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
        className="object-cover transition-transform duration-300 group-hover:scale-105"
      />
      <span className="absolute inset-0 flex items-center justify-center bg-black/20 text-white">
        <PlayCircle className="size-12 drop-shadow" />
      </span>
    </span>
    <span className="flex flex-1 flex-col gap-2 p-4">
      <span className="text-foreground flex items-start justify-between gap-3 text-base font-semibold">
        {title}
        <ArrowUpRight className="text-primary mt-1 size-4 shrink-0" />
      </span>
      <span className="text-ring text-sm leading-relaxed">{text}</span>
    </span>
  </Link>
);

export default function PodcastContent() {
  const t = useTranslations("podcast");

  const topicKeys = [
    "floorplans",
    "marketing",
    "financing",
    "tour",
    "law",
    "investment",
  ] as const;

  const videoKeys = [
    { key: "video40", src: section40 },
    { key: "video39", src: section39 },
    { key: "video38", src: section38 },
    { key: "video37", src: section37 },
  ] as const;

  return (
    <div className="[--h1-weight:500] [--heading-scale:0.6]">
      <Header
        title={t("head.title")}
        subtitle={t("head.lead")}
        containerClass="gs--page-column gs--page-column--narrow"
      />

      <div className="gs--page-column flex flex-col gap-14 py-12 sm:py-16">
        <section className="grid items-center gap-8 lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)]">
          <div className="flex flex-col gap-5">
            <span className="bg-primary w-fit px-4 py-1 text-sm font-medium tracking-wide text-white uppercase">
              Spacelutions
            </span>
            <TitleH2 title={t("feature.title")} />
            <p className="text-ring text-lg leading-relaxed">
              {t("feature.text")}
            </p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <ListenButton
                href={SPACELUTIONS_YOUTUBE_URL}
                icon={<Youtube size={28} />}
                label={t("listen.youtube.label")}
                description={t("listen.youtube.description")}
              />
              <ListenButton
                href={SPACELUTIONS_SITE_URL}
                icon={<Globe2 size={28} />}
                label={t("listen.spacelutions.label")}
                description={t("listen.spacelutions.description")}
              />
            </div>
          </div>

          <Link
            href={SPACELUTIONS_YOUTUBE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group border-input relative block overflow-hidden rounded-lg border bg-black shadow-sm"
          >
            <Image
              src={header}
              alt={t("feature.imageAlt")}
              priority
              sizes="(min-width: 1024px) 50vw, 100vw"
              className="h-auto w-full transition-transform duration-300 group-hover:scale-105"
            />
            <span className="absolute inset-0 flex items-center justify-center bg-black/20 text-white">
              <span className="flex size-20 items-center justify-center rounded-full bg-black/45">
                <PlayCircle className="size-12" />
              </span>
            </span>
          </Link>
        </section>

        <section className="gs--partner-section">
          <TitleH2 title={t("intro.title")} />
          <p className="gs--partner-section__text text-ring">
            {t.rich("intro.text", {
              br: () => <br />,
              bold: (children) => <strong>{children}</strong>,
            })}
          </p>
        </section>

        <ScrollReveal>
          <section className="gs--partner-section">
            <TitleH2 title={t("videos.title")} />
            <p className="gs--partner-section__text text-ring">
              {t("videos.intro")}
            </p>
            <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {videoKeys.map(({ key, src }) => (
                <VideoCard
                  key={key}
                  src={src}
                  alt={t(`videos.items.${key}.imageAlt`)}
                  title={t(`videos.items.${key}.title`)}
                  text={t(`videos.items.${key}.text`)}
                  href={t(`videos.items.${key}.link`)}
                />
              ))}
            </div>
          </section>
        </ScrollReveal>

        <ScrollReveal>
          <section className="gs--partner-section">
            <TitleH2 title={t("topics.title")} />
            <p className="gs--partner-section__text text-ring">
              {t("topics.intro")}
            </p>
            <div className="mt-2 grid grid-cols-1 gap-4 md:grid-cols-2">
              {topicKeys.map((key) => (
                <TopicCard
                  key={key}
                  title={t(`topics.items.${key}.title`)}
                  text={t(`topics.items.${key}.text`)}
                />
              ))}
            </div>
          </section>
        </ScrollReveal>

        <ScrollReveal>
          <section className="gs--partner-section">
            <TitleH2 title={t("why.title")} />
            <p className="gs--partner-section__text text-ring">
              {t.rich("why.text", {
                br: () => <br />,
                bold: (children) => <strong>{children}</strong>,
              })}
            </p>
          </section>
        </ScrollReveal>

        <ScrollReveal>
          <section className="gs--partner-section gs--partner-section--last">
            <TitleH2 title={t("stayConnected.title")} />
            <p className="gs--partner-section__text text-ring">
              {t("stayConnected.text")}
            </p>
          </section>
        </ScrollReveal>
      </div>
    </div>
  );
}
